import { ReactNode } from 'react'
import { AnimatePresence, motion, MotionConfig } from 'motion/react'
import { Sheet, SheetContent } from '../../@shadcn/components/ui/sheet'
import GiftCardCloseButton from './GiftCardCloseButton'

// The sheet content itself carries no chrome — the white rounded panel is the inner div. That split
// is what lets the floating close button sit above the panel instead of being clipped by it.
const SHEET_SHELL_CLASS =
  'ui-flex ui-max-h-[85%] ui-flex-col !ui-p-0 !ui-gap-0 !ui-overflow-visible !ui-bg-transparent !ui-shadow-none'
// overflow-hidden + radius live HERE because this is the element whose height actually changes —
// Motion measures it via FLIP when `screenKey` swaps the screen inside.
const SHEET_PANEL_CLASS =
  'ui-relative ui-flex ui-min-h-0 ui-flex-1 ui-flex-col ui-overflow-hidden ui-rounded-t-2xl ui-bg-background ui-shadow-lg ui-antialiased'
const SHEET_OVERLAY_CLASS = '!ui-bg-black/40'
const SCREEN_CLASS = 'ui-flex ui-w-full ui-min-h-0 ui-flex-col'

export interface GiftCardSheetProps {
  isOpen: boolean
  onOpenChange: (open: boolean) => void
  container?: HTMLElement | null
  showCloseButton?: boolean
  onClose: () => void
  /**
   * Identity of the screen currently in `children`. Changing it cross-fades the screens and morphs
   * the panel height between them instead of closing and reopening the sheet.
   */
  screenKey: string
  children: ReactNode
}

const GiftCardSheet = ({
  isOpen,
  onOpenChange,
  container,
  showCloseButton = false,
  onClose,
  screenKey,
  children
}: GiftCardSheetProps) => (
  <Sheet open={isOpen}  modal={false}>
    <SheetContent
      side='bottom'
      container={container}
      withinContainer={Boolean(container)}
      className={SHEET_SHELL_CLASS}
      overlayClassName={SHEET_OVERLAY_CLASS}
    >
      <MotionConfig transition={{ type: 'spring', bounce: 0, duration: 0.45 }}>
        {/* `layout` here too, not just on the panel: the button sits above it in the same column,
            so the panel morphing height moves the button's real position on screen every bit as
            much as the panel's own edges — without `layout` it would jump straight there instead
            of easing along with the panel beneath it. */}
        {showCloseButton && <GiftCardCloseButton onClose={onClose} />}
        {/* `layout` on the panel and `layout="position"` on the screen: the panel is what resizes,
            the screen only rides along so the FLIP scale never stretches its text. */}
        <motion.div layout className={SHEET_PANEL_CLASS}>
          {/* popLayout pulls the outgoing screen out of flow, so the panel's height is the incoming
              screen's height right away and the morph animates to it. */}
          <AnimatePresence initial={false} mode='popLayout'>
            <motion.div
              key={screenKey}
              layout='position'
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.2 }}
              className={SCREEN_CLASS}
            >
              {children}
            </motion.div>
          </AnimatePresence>
        </motion.div>
      </MotionConfig>
    </SheetContent>
  </Sheet>
)

export default GiftCardSheet
